import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { Section, Field, Empty } from "../common";

const EMPTY = { name: "", member_ids: [], schedule: "", notes: "" };

function WorkerRates() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/worker-rates").then((r) => setRows(r.data)).catch(() => {}); }, []);
  const save = async (x, v) => {
    if (Number(v) === Number(x.rate)) return;
    try { await api.put(`/worker-rates/${x.id}`, { rate: Number(v) || 0 }); setRows(rows.map((r) => (r.id === x.id ? { ...r, rate: Number(v) || 0 } : r))); toast.success(t("Rate saved", "Pago guardado")); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={t("Hourly pay per worker", "Pago por hora de cada trabajador")} testid="worker-rates">
      <p className="px-4 pt-3 text-xs text-slate-500">{t("Only people allowed to see prices can see or change this.", "Solo quienes tienen permiso de ver precios pueden verlo o cambiarlo.")}</p>
      {rows.map((x) => (
        <label key={x.id} className="px-4 py-2 flex items-center gap-3 text-sm"><span className="flex-1 truncate">{x.name} <span className="text-xs text-slate-400">· {x.role}</span></span>
          <span className="text-slate-500">$</span><input className="inp !w-24" type="number" min="0" step="any" defaultValue={x.rate} onBlur={(e) => save(x, e.target.value)} data-testid={`worker-rate-${x.id}`} /><span className="text-xs text-slate-500">/h</span></label>
      ))}
    </Section>
  );
}

export function CrewsManager() {
  const { t } = useLang();
  const { can } = useAuth();
  const [crews, setCrews] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = () => api.get("/crews").then((r) => setCrews(r.data));
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data.filter((x) => !x.removed))).catch(() => {}); }, []);
  const toggle = (id) => setF({ ...f, member_ids: f.member_ids.includes(id) ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] });
  const save = async () => {
    try { f.id ? await api.put(`/crews/${f.id}`, f) : await api.post("/crews", f); setF(EMPTY); load(); toast.success(t("Crew saved", "Cuadrilla guardada")); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => { if (window.confirm(t("Delete crew?", "¿Eliminar cuadrilla?"))) { await api.delete(`/crews/${c.id}`); load(); } };
  return (
    <div className="grid gap-3" data-testid="crews-manager">
      <Section title={f.id ? t("Edit crew", "Editar cuadrilla") : t("New crew", "Nueva cuadrilla")} testid="crew-form">
        <div className="p-4 grid gap-3">
          <Field label={t("Crew name", "Nombre de la cuadrilla")}><input className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="crew-name-input" /></Field>
          <div className="flex flex-wrap gap-2">{users.map((x) => <button key={x.id} type="button" onClick={() => toggle(x.id)} className={f.member_ids.includes(x.id) ? "btn-primary btn-sm" : "btn-outline btn-sm"} data-testid={`crew-member-${x.id}`}>{x.name} · {x.role}</button>)}</div>
          <Field label={t("Schedule (days / hours)", "Horario (días / horas)")}><input className="inp" value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} data-testid="crew-schedule-input" /></Field>
          <Field label={t("Notes", "Notas")}><textarea className="inp" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="crew-notes-input" /></Field>
          <p className="text-xs text-slate-500">{t("Employees are added in Team. Crew-role workers never see prices, profit or financial data.", "Los empleados se agregan en Equipo. Los trabajadores con rol Cuadrilla nunca ven precios, ganancias ni datos financieros.")}</p>
          <button className="btn-primary" disabled={!f.name.trim()} onClick={save} data-testid="crew-save-btn">{t("Save crew", "Guardar cuadrilla")}</button>
        </div>
      </Section>
      {!crews.length && <Empty text={t("No crews yet", "Aún no hay cuadrillas")} testid="crews-empty" />}
      {crews.map((c) => (
        <div key={c.id} className="card p-4 flex items-start gap-3" data-testid={`crew-${c.id}`}>
          <Users className="w-5 h-5 text-[#D9531E] mt-1" />
          <button className="flex-1 text-left" onClick={() => setF(c)} data-testid={`crew-edit-${c.id}`}><div className="font-bold">{c.name}</div><div className="text-xs text-slate-500">{c.members.join(", ") || "—"}{c.schedule && ` · ${c.schedule}`}</div></button>
          <button className="btn-ghost btn-sm" onClick={() => del(c)} data-testid={`crew-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {can("view_prices") && <WorkerRates />}
    </div>
  );
}
